import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  CircularProgress,
  Button,
  Chip,
} from '@mui/material';
import {
  Event as EventIcon,
  Article as ArticleIcon,
  Person as PersonIcon,
  Group as GroupIcon,
} from '@mui/icons-material';
import api from '../../services/api';
import { formatDistanceToNow } from 'date-fns';
import { useNavigate } from 'react-router-dom';

const ActivityFeed = ({ userId = null, limit = 20 }) => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchActivities();
  }, [userId, page]);

  const fetchActivities = async () => {
    try {
      setLoading(true);
      const endpoint = userId ? '/activities/my' : '/activities';
      const response = await api.get(endpoint, {
        params: { page, limit },
      });
      
      if (page === 1) {
        setActivities(response.data.data);
      } else {
        setActivities((prev) => [...prev, ...response.data.data]);
      }
      
      setHasMore(response.data.pagination.page < response.data.pagination.pages);
    } catch (error) {
      console.error('Error fetching activities:', error);
    } finally {
      setLoading(false);
    }
  };

  const getActivityIcon = (type) => {
    switch (type) {
      case 'event_created':
      case 'event_registered':
      case 'event_attended':
        return <EventIcon />;
      case 'post_created':
      case 'post_liked':
      case 'post_commented':
        return <ArticleIcon />;
      case 'user_joined':
      case 'group_joined':
        return <GroupIcon />;
      default:
        return <PersonIcon />;
    }
  };

  const getActivityColor = (type) => {
    if (type.includes('event')) return 'primary';
    if (type.includes('post')) return 'secondary';
    if (type.includes('user') || type.includes('group')) return 'success';
    return 'default';
  };

  const handleActivityClick = (activity) => {
    if (activity.link) {
      navigate(activity.link);
    }
  };

  if (loading && activities.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (activities.length === 0) {
    return (
      <Card sx={{ backgroundColor: '#ffffff', border: '1px solid #e5e7eb' }}>
        <CardContent>
          <Typography variant="body2" color="text.secondary" align="center">
            No activities yet
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ backgroundColor: '#ffffff', border: '1px solid #e5e7eb' }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          {userId ? 'My Activity' : 'Recent Activity'}
        </Typography>
        <List>
          {activities.map((activity) => (
            <ListItem
              key={activity._id}
              button
              onClick={() => handleActivityClick(activity)}
              sx={{ mb: 1, borderRadius: 1 }}
            >
              <ListItemAvatar>
                <Avatar
                  src={activity.user?.profileImage}
                  sx={{ bgcolor: `${getActivityColor(activity.type)}.main` }}
                >
                  {getActivityIcon(activity.type)}
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body1">
                      {activity.user?.name || 'Someone'} {activity.title}
                    </Typography>
                    <Chip
                      label={activity.type.replace('_', ' ')}
                      size="small"
                      color={getActivityColor(activity.type)}
                      variant="outlined"
                    />
                  </Box>
                }
                secondary={
                  <>
                    <Typography variant="body2" color="text.secondary">
                      {activity.description}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatDistanceToNow(new Date(activity.createdAt), {
                        addSuffix: true,
                      })}
                    </Typography>
                  </>
                }
              />
            </ListItem>
          ))}
        </List>
        {hasMore && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Button
              variant="outlined"
              onClick={() => setPage((prev) => prev + 1)}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : 'Load More'}
            </Button>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default ActivityFeed;

